import { useEffect, useState } from "react";
import { UserTabs } from "../../users/components/UserTabs";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Card, CardBody } from "../../../components/ui";
import { BLOB_IMAGE_URL } from "../../../config/appConfig";
import { BlobContainerConsts } from "../../../models/constants/blobContainerConsts";
import { copyToClipboard } from "../../../utils/clipboard";
import { toastService as toast, toastService } from "../../../components/ui/toast/toast.service";
import { ConfirmationModal } from "../../../components/ui/modals/ConfirmationModal";
import { revertUserDepositById } from "../../transaction/services/transactions.service";
import depositv1Service from "../services/deposit.service";
import localization from "../../../config/localization";
import userDeviceStore from "../../../store/device.store";
import { DeviceType } from "../../../models/constants/deviceType";
import { useAppDispatch } from "../../../store/hooks";
import { changeLoaderState } from "../../../reducers/appStateReducer";
import { increaseInboxUnreadCount } from "../../users/reducers/userProfileReducer";
import { AccessMode } from "../../../models/constants/accessMode";

interface DepositUnderApprovalProps {
  transactionId?: string;
  amount?: string;
  selectedDepositTypeName?: string;
  isDepositRevertAllowed?: boolean;
  selectedDepositTypeImage?: string;
  depositRequestedId?: string;
}

export function DepositUnderApproval() {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<string>("deposit");
  const navigate = useNavigate();
  const deviceType = userDeviceStore((state) => state.deviceType);
  const isWebDevice = deviceType === DeviceType.Web;
  const [showDepositRevertModal, setShowDepositRevertModal] =
    useState<boolean>(false);
  const [depositData, setDepositData] =
    useState<DepositUnderApprovalProps | null>(null);
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(changeLoaderState(true));
    setTimeout(() => {
      dispatch(increaseInboxUnreadCount());
    }, 1000);
  }, []);

  useEffect(() => {
    const init = async () => {
      if (location.state) {
        setDepositData(location.state as DepositUnderApprovalProps);
        dispatch(changeLoaderState(false));
        return;
      }

      const details =
        await depositv1Service.getUserPendingDepositsWithDetails();
      if (details && details.length && details.length > 0) {
        const depositdata: DepositUnderApprovalProps = {
          transactionId: details?.[0]?.transactionid,
          amount: details?.[0]?.amount?.toString(),
          selectedDepositTypeName: details?.[0]?.bankname,
          isDepositRevertAllowed: details?.[0]?.isrevertallowed,
          selectedDepositTypeImage: `${BLOB_IMAGE_URL}${BlobContainerConsts.Banks}/${details?.[0]?.logourl}`,
          depositRequestedId: details?.[0]?.id,
        };
        setDepositData(depositdata);
      }
      else {
        navigate("/deposithistory");
      }
      dispatch(changeLoaderState(false));
    };

    init();
  }, [location.state, navigate]);

  const onTabChange = (tab: string) => {
    navigate(`/${tab}`);
    setActiveTab(tab);
  };

  const handleCopy = async () => {
    let accessMode = userDeviceStore.getState().accessModeSource.sourceType ?? AccessMode.Web;
    await copyToClipboard(accessMode, depositData?.transactionId, {
      onSuccess: () => {
        toastService.success(localization.refid_success_copied);

        setTimeout(() => { }, 5000);
      },
      onError: () => {
        toastService.error(localization.refid_copy_failed);
      },
    });
  };

  const handleRevertDeposit = async () => {
    if (depositData?.depositRequestedId) {
      var response = await revertUserDepositById(
        depositData?.depositRequestedId,
      );
      if (response) {
        toast.success(localization.deposit_reverted_successfully);
        navigate("/deposit");
      } else {
        setShowDepositRevertModal(false);
        navigate("/deposithistory");
      }
    }
  };

  if (!depositData) {
    return null;
  }

  const hasRevertAction = Boolean(depositData?.isDepositRevertAllowed);

  const approvalContent = (
    <div className={isWebDevice ? "m-4 flex justify-center" : ""}>
      <div
        className={
          isWebDevice
            ? "w-full max-w-4xl px-6 py-8"
            : "border-2 border-[#141414] rounded-lg m-4 px-4 py-6"
        }
      >
        <div className="flex justify-center mt-5 mb-6">
          <img
            src={"/images/img-clock.svg"}
            alt="Deposit Under Approval"
            className={isWebDevice ? "size-18" : "size-24"}
          />
        </div>

        <h2
          className={
            isWebDevice
              ? "text-center text-base font-medium text-white"
              : "text-center text-lg font-medium text-white"
          }
        >
          {localization.deposit_under_approval}
        </h2>

        <div
          className={
            isWebDevice
              ? "mt-3 text-center text-4xl leading-none font-semibold text-white"
              : "mt-5 text-center text-3xl font-semibold text-white"
          }
        >
          {`\u20B9${Number(depositData?.amount)?.toFixed(2)}`}
        </div>

        <div
          className={
            isWebDevice
              ? "mt-8 mx-auto w-full max-w-lg space-y-3"
              : "mt-8 space-y-5 text-sm"
          }
        >
          <div className="flex justify-between items-center text-[#A9A9A9]">
            <span
              className={
                isWebDevice ? "text-base font-medium" : "text-sm font-medium"
              }
            >
              {localization.reference_id || "Reference ID"}
            </span>
            <div className="flex items-center gap-2 text-white font-medium min-w-0 max-w-[65%] justify-end">
              <span
                className={
                  isWebDevice
                    ? "text-base font-medium text-white truncate"
                    : "text-white font-medium text-base break-all text-right"
                }
              >
                {depositData?.transactionId}
              </span>
              <span>
                <img
                  src={"/icons/ic-copy.svg"}
                  alt="Copy"
                  className={
                    isWebDevice
                      ? "w-4 h-4 cursor-pointer"
                      : "w-5 h-5 cursor-pointer"
                  }
                  onClick={handleCopy}
                />
              </span>
            </div>
          </div>

          <div className="flex justify-between items-center text-zinc-400">
            <span
              className={
                isWebDevice ? "text-base font-medium" : "text-sm font-medium"
              }
            >
              {localization.deposit_type}
            </span>
            <div className="flex items-center gap-2 text-white font-medium min-w-0 max-w-[65%] justify-end">
              <span>
                <img
                  src={depositData?.selectedDepositTypeImage}
                  alt="Deposit Type"
                  className={
                    isWebDevice
                      ? "w-4 h-4 object-contain"
                      : "w-6 h-6 object-contain"
                  }
                />
              </span>
              <span
                className={
                  isWebDevice
                    ? "text-base font-medium truncate"
                    : "text-sm text-right break-words"
                }
              >
                {depositData?.selectedDepositTypeName}
              </span>
            </div>
          </div>
        </div>

        <div
          className={
            isWebDevice
              ? hasRevertAction
                ? "mt-6 grid grid-cols-1 sm:grid-cols-2 gap-6"
                : "mt-6 max-w-[420px] mx-auto"
              : "mt-10 space-y-4"
          }
        >
          <Button
            type="submit"
            className={`${isWebDevice ? "w-full h-14 text-base  rounded-md" : "w-full py-4 text-sm"} text-white font-medium bg-primary-gradient`}
            onClick={() => navigate("/deposithistory")}
          >
            <div className="flex items-center justify-center gap-2">
              <img
                src={"/icons/ic-rotate.svg"}
                alt="save-image"
                className={isWebDevice ? "w-4 h-4" : "w-5 h-5"}
              />
              <span>
                {localization.check_deposit_status}
              </span>
            </div>
          </Button>

          {depositData?.isDepositRevertAllowed && (
            <Button
              type="button"
              className={`${isWebDevice ? "w-full h-14 text-base border rounded-md" : "w-full py-4 text-sm border-2 rounded-lg"} text-white font-medium border-[#06876D] bg-transparent`}
              onClick={() => setShowDepositRevertModal(true)}
            >
              <div className="flex items-center justify-center gap-2">
                <img
                  src={"/icons/ic-revert.svg"}
                  alt="save-image"
                  className={isWebDevice ? "w-4 h-4" : "w-5 h-5"}
                />
                <span>
                  {localization.revert_deposit_request}
                </span>
              </div>
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <UserTabs activeTab={activeTab} onTabChange={onTabChange} />
      {isWebDevice ? (
        <Card>
          <CardBody showGradient={true}>{approvalContent}</CardBody>
        </Card>
      ) : (
        approvalContent
      )}

      {showDepositRevertModal && (
        <ConfirmationModal
          isOpen={showDepositRevertModal}
          onConfirm={() => {
            handleRevertDeposit();
          }}
          onCancel={() => {
            setShowDepositRevertModal(false);
          }}
          title={localization.confirm_revert_deposit}
          description={`\u20B9${Number(depositData?.amount)?.toFixed(2)}`}
          descriptionClassName="text-center text-[34px] font-medium"
          icon={"/icons/ic-info.svg"}
          blurColor="bg-[#FFE6CC]"
          confirmText={localization.yes || "Yes"}
          cancelText={localization.no || "No"}
        />
      )}
    </div>
  );
}
